/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { PerformanceReportConfigurationForm } from './PerformanceReportConfigurationForm'
import { Button, Form, Icon, Segment } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUser from '../../Shared/UserProvider/useUser'
import {
    deletePerformanceReportConfiguration, isProviderAPos,
    updatePerformanceReportConfiguration,
} from '../../../services/Provider/Provider.service'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom-v5-compat'
import { ultyToast } from '../../Shared/UltyToast/UltyToast'

const PerformanceReportConfigurationUpdate = ({performanceReportConfiguration}) => {
    const [t] = useTranslation()
    const [performanceReportConfigurationUpdate, setPerformanceReportConfigurationUpdate] = useState(performanceReportConfiguration)

    const {user} = useUser()
    const queryClient = useQueryClient()
    const [searchParams] = useSearchParams()
    const selectedPosId = searchParams.get('posId')

    const providerId = user.isLoggedInOwnScope ?
      user.loginContext.providers
        .filter(isProviderAPos)
        .find(provider => provider.object.id === selectedPosId)?.id :
      user.provider.id

    const onError = () => {
        ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
    }

    const onSuccess = async () => {
        ultyToast({ type: 'success', title: t('global.registerSuccess') })
        await queryClient.invalidateQueries({
            queryKey: ['providers', providerId, 'performanceReportConfigurations'],
        })
    }

    const deleteMutation = useMutation({
        mutationFn: (performanceReportConfiguration) => deletePerformanceReportConfiguration(performanceReportConfiguration),
        onError,
        onSuccess,
    })

    const updateMutation = useMutation({
        mutationFn: (performanceReportConfiguration) => updatePerformanceReportConfiguration(performanceReportConfiguration),
        onError,
        onSuccess,
    })

    const isPending = deleteMutation.isPending || updateMutation.isPending

    const canUpdatePerformanceReportConfiguration =
      !isPending &&
      performanceReportConfigurationUpdate.recipients.length > 0 &&
      performanceReportConfigurationUpdate.posIds.length > 0 &&
      (performanceReportConfigurationUpdate.options.performance.display ||
        performanceReportConfigurationUpdate.options.bestsellers.display ||
        performanceReportConfigurationUpdate.options.unavailableItems?.display ||
        performanceReportConfigurationUpdate.options.counterPerformance.display)

    return (
      <Segment color="teal">
          <Form>
              <PerformanceReportConfigurationForm
                performanceReportConfiguration={performanceReportConfigurationUpdate}
                onChange={(newValues) => {
                    setPerformanceReportConfigurationUpdate(prev => ({
                        ...prev,
                        ...newValues,
                    }))
                }}
              />

              <div css={css`
                  text-align: right;
                  margin-top: 1rem;

                  & :last-child {
                      margin-right: 0;
                  }
              `}>
                  <Button
                    color="red"
                    type="button"
                    loading={isPending}
                    disabled={isPending}
                    onClick={() => deleteMutation.mutate(performanceReportConfiguration)}
                  >
                      <Icon name="delete"/>
                      {t('global.delete')}
                  </Button>
                  <Button
                    color="teal"
                    type="submit"
                    loading={isPending}
                    disabled={!canUpdatePerformanceReportConfiguration}
                    onClick={() => updateMutation.mutate(performanceReportConfigurationUpdate)}
                  >
                      <Icon name="save"/>
                      {t('global.save')}
                  </Button>
              </div>
          </Form>
      </Segment>
    )
}

export { PerformanceReportConfigurationUpdate }
